import React, { useState } from "react";
import { Box, Modal, Typography, Button, CircularProgress, Grid, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";

import type { RootState } from "../../../store/state/rootReducer";
import type { AppDispatch } from "../../../store/state/rootStore";
import { loadFolderStories, resetFolderStories, setFolderStoriesPageView } from "../../../store/actions/folders";
import useMixPanel from "../../../lib/mixpanel";
import TSSecondaryButton from "../../../components/common/buttons/TSSecondaryButton";
import { loadProjectStories, resetStoriesProject, setProjectStoriesPageView } from "../../../store/actions/projects";
import { clearSelectedStories, clearStories, loadStoriesMyInterview } from "../../../store/state/stories/actions";
import { notifyError, notifySuccess } from "../../../store/actions/notify";

interface BulkDeleteModalProps {
  showModal: boolean;
  onCancel: () => void;
}

export const BulkDeleteModal = ({ showModal, onCancel }: BulkDeleteModalProps) => {
  const { project_id: projectId, folder_id: folderId } = useParams();
  const [deleteValidationText, setDeleteValidationText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const selectedStories = useSelector((state: RootState) => state.stories.selectedStories);
  const localRecordingsEnabled = useSelector(
    (state: RootState) =>
      state.featureFlags.loaded &&
      (state.featureFlags.flags?.localRecordings === true || state.featureFlags.flags?.dailyCallV2 === true)
  );
  const selectedStoriesIds = selectedStories.map((story) => story._id);
  const isMyInterviews = useLocation().pathname.includes("/myInterviews") ? "myInterviews" : null;
  const isPlural = selectedStories.length > 1;

  const refreshData = async () => {
    try {
      if (folderId) {
        await dispatch(resetFolderStories());
        await dispatch(setFolderStoriesPageView({ currentPage: 1 }));
        await dispatch(loadFolderStories({ folderId }));
      }
      if (projectId && !folderId) {
        await dispatch(resetStoriesProject());
        await dispatch(setProjectStoriesPageView({ currentPage: 1 }));
        await dispatch(loadProjectStories({ projectId }));
      }
      if (isMyInterviews) {
        await dispatch(clearStories());
        await dispatch(loadStoriesMyInterview());
      }
      dispatch(clearSelectedStories());
      onCancel();
    } catch (e) {
      setIsDeleting(false);
      console.log(e);
    }
  };

  const handleBulkDelete = async () => {
    try {
      useMixPanel("bulk_delete_stories", { stories: selectedStoriesIds });
      setIsDeleting(true);

      const failedDeletes: string[] = [];

      for (const storyId of selectedStoriesIds) {
        try {
          await axios.delete(`/stories/${storyId}`);
        } catch (error) {
          console.error(`Failed to delete story with ID: ${storyId}`, error);
          failedDeletes.push(storyId);
        }
      }

      await refreshData();

      if (failedDeletes.length > 0) {
        dispatch(notifyError(`Error deleting ${failedDeletes.length} recording(s). Please try again.`));
      } else {
        dispatch(notifySuccess("Recordings deleted successfully"));
      }

      setIsDeleting(false);
    } catch (error) {
      console.error("Bulk delete failed", error);
      dispatch(notifyError("Error deleting interviews"));
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      open={showModal}
      onClose={onCancel}
      aria-labelledby="modal-bulk-delete-title"
      aria-describedby="modal-bulk-delete-description"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <>
        <Box
          sx={{
            background: "white",
            maxWidth: "650px",
            width: "80%",
            padding: "24px",
            borderRadius: "5px",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "rgba(0, 0, 0, 0.87)",
              fontSize: "20px",
              lineHeight: "32px",
              fontWeight: "bold",
              marginBottom: "16px",
            }}
          >
            Delete Interview
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(0, 0, 0, 0.87)",
              fontSize: "16px",
              lineHeight: "16px",
              fontWeight: 600,
              marginBottom: "16px",
            }}
          >
            This action is permanent and cannot be undone.
          </Typography>
          <ul style={{ color: "#464C53", fontWeight: "400", marginBottom: "0.5rem" }}>
            <li>
              All the <b>clips</b> created from {isPlural ? "these interviews" : "this interview"} will be deleted.
            </li>
            {localRecordingsEnabled && (
              <li>
                All <b>local recording files and chunks</b> uploaded for{" "}
                {isPlural ? "these interviews" : "this interview"} will also be deleted.
              </li>
            )}
            <li>
              If the {isPlural ? "interviews are" : "interview is"} <b>shared publicly</b>, deleting{" "}
              {isPlural ? "them" : "it"} will remove {isPlural ? "them" : "it"} from public view.
            </li>
            <li>
              If the {isPlural ? "interviews are" : "interview is"} in a <b>public gallery</b>, deleting{" "}
              {isPlural ? "them" : "it"} here won’t remove {isPlural ? "them" : "it"} from the gallery. They will remain
              visible until you republish the gallery.
            </li>
          </ul>
          {selectedStories.length > 0 && (
            <>
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(0, 0, 0, 0.87)",
                  fontSize: "16px",
                  lineHeight: "24px",
                  fontWeight: 400,
                  marginBottom: "5px",
                  marginTop: "16px",
                }}
              >
                To proceed, please type <b>DELETE</b> in all caps.
              </Typography>

              <Box display="flex" flexDirection="column" gap={3} sx={{ marginBottom: "1.5rem" }}>
                <TextField
                  placeholder="DELETE"
                  size="small"
                  sx={{
                    width: "234px",
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                  }}
                  value={deleteValidationText}
                  onChange={(e) => setDeleteValidationText(e.target.value)}
                />
              </Box>
            </>
          )}
          <Box sx={{ width: "100%", gap: 30 }}>
            <Grid container sx={{ alignContent: "center" }}>
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <TSSecondaryButton text="Cancel" onClick={onCancel} styles={{ marginRight: "10px" }} />
                <Button
                  onClick={handleBulkDelete}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#DD2D2D",
                    width: "180px",
                    "&:hover": {
                      outline: "none",
                    },
                    "&:focus": {
                      outline: "none",
                    },
                  }}
                  disabled={!(deleteValidationText === "DELETE")}
                  variant="contained"
                  color="error"
                >
                  {isDeleting ? <CircularProgress size="20px" /> : "Delete permanently"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </>
    </Modal>
  );
};
